<template>
  <div class="h-100 d-flex flex-column align-center justify-space-around fit-content mx-auto ga-5">
    <ConnectingLine
      :startElementId="`command-${system.index}-${event.index}`"
      :endElementId="`system-${system.index}`"
      :refreshKey="refreshKey"
      offsetParentId="domain-model"
    />
    <div :id="`command-${system.index}-${event.index}`" ref="commandEl">
      <div
        ref="cardElement"
        class="model-container"
        v-if="system.actions[event.index]"
        :style="{
          minHeight: cardHeight,
          marginTop: '20px'
        }"
      >
        <div
          class="card-item hover-container height-transition-hover-container"
          :data-card-id="system.actions[event.index].id"
          :style="{
            'border-color': cardTypeColor(commandCardType),
            '--transitionContainerHeight': !system.actions[event.index].schemaData.hasSchema || !showEntities ? '24px' : '16px'
          }"
        >
          <beam-card
            v-if="!system.actions[event.index].schemaData.hasSchema"
            :event="event"
            :card="system.actions[event.index]"
          />
          <div v-else>
            <div>
              <text-editor
                class="pa-1"
                :id="system.actions[event.index].id"
                :text="system.actions[event.index].description"
                :disabled="store.state.isWorkflowDisabled"
                @text-update="(e) => updateCardDescription(e, system.actions[event.index].id)"
                @move-focus="moveFocus"
                @keydown.native.stop=""
                :borderColor=" cardTypeColor(commandCardType)"
              />
            </div>
            <domain-model-write-model-fields
              v-if="system.actions[event.index].schemaData.fields.length > 0 && showEntities"
              class="py-2 fields"
              :fields="system.actions[event.index].schemaData.fields"
              :depth="1"
              :maxDepth="3"
              :path="[]"
              :event="event"
              :cardId="system.actions[event.index].id"
              :schemaId="system.actions[event.index].schemaId"
              :disabled="store.state.isWorkflowDisabled"
              :checkMissingEntity="isMissingEntity"
              :checkEntityMismatch="isSelectedEntityMismatch"
              :getEntityFieldRelatedEntity="getEntityFieldRelatedEntity"
              :fixEntityMismatch="fixEntityReferenceMismatch"
              :rootEntityData="currentSystemEntitiesData?.rootEntity"
            />
          </div>
          <domain-model-card-toolbar
            v-if="!store.state.isWorkflowDisabled"
            :showAi="!system.actions[event.index].schemaData.hasSchema"
            :showFilters="true"
            :event="event"
            :card="system.actions[event.index]"
            :menuPositioningActivator="cardElement"
          />
        </div>
      </div>
      <v-btn
        v-else-if="commandCardType"
        class="add-card-btn"
        variant="elevated"
        :style="{ '--border-color': cardTypeColor(commandCardType) }"
        block
        data-cy="add-command-btn"
        @click="createCard(event.id, commandCardType.id, `<p>Command for ${event.description}</p>`)"
        :disabled="store.state.isWorkflowDisabled"
      >
        <v-icon size="large">mdi-plus</v-icon>
      </v-btn>
      <aggregate-type-selector
        v-else
        :cardTypes="cardTypes"
        :type="DomainModelRole.COMMAND"
      />
    </div>
  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { computed, nextTick, onBeforeUnmount, onMounted, watch, ref } from 'vue'
import { useRoute } from 'vue-router'
import BeamCard from '@/components/BeamCard'
import TextEditor from '@/components/TextEditor.vue'
import ConnectingLine from '@/components/ConnectingLine'
import AggregateTypeSelector from '@/components/AggregateTypeSelector'
import DomainModelCardToolbar from '@/components/DomainModelCardToolbar'
import DomainModelWriteModelFields from '@/components/DomainModelWriteModelFields'
import workflowApi from '@/api/workflowApi'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import useDomainModel from '@/composables/useDomainModel'
import { useSortFields } from '@/composables/useSortFields'
import { DomainModelRole } from '@/constants/cardTypes'

import safeStructuredClone from '@/utils/clone'
import { fieldHasNoMatchingEntityField } from '@/shared/utils/fieldEntityMatch'

const {
  system,
  event,
  refreshKey,
  currentSystemEntitiesData,
  showEntities
} = defineProps({
  system: Object,
  event: Object,
  refreshKey: Number,
  currentSystemEntitiesData: Object,
  showEntities: Boolean
})

const store = useStore()
const route = useRoute()

const workflow = computed(() => store.state.workflow.workflow)
const svgGraph = computed(() => store.getters['svgGraphData/svgGraphData'])
const cardTypes = computed(() => store.state.cardTypes.cardTypes)
const commandCardType = computed(() => diagramHelpers.methods.getCommandCardType(cardTypes.value))
const cardHeight = computed(() => system.actions[event.index] ? getCommandCardHeight(event, system.actions[event.index]) : undefined)

const {
  cardTypeColor,
  getCommandCardHeight,
  getMatchingFieldInEntity,
  createCard
} = useDomainModel()
const { initSortable, destroySortable } = useSortFields()

const commandEl = ref(null)
const cardElement = ref(null)

const isMissingEntity = (commandField) =>
  fieldHasNoMatchingEntityField(
    currentSystemEntitiesData?.rootEntity?.id,
    { name: commandField },
    workflow.value.schemas || []
  )

const moveFocus = (_, target) => {
  target.firstChild.blur()
}

const updateCardDescription = (html, itemId) => {
  workflowApi.updateCardsMutation(route.params.id, [{ cardId: itemId, description: html }])
}

const isSelectedEntityMismatch = (field) => {
  const matchingFieldInEntity = getMatchingFieldInEntity(currentSystemEntitiesData?.rootEntity, field.name)

  if (
    !matchingFieldInEntity ||
    (!field.relatedEntityId && !matchingFieldInEntity?.relatedEntityId) ||
    field.relatedEntityId === matchingFieldInEntity?.relatedEntityId
  ) {
    return false
  }

  const commandFieldRelatedEntity = field.relatedEntityId
    ? workflow.value.schemas?.find((entity) => entity.id === field.relatedEntityId)
    : true
  const entityFieldRelatedEntity = matchingFieldInEntity?.relatedEntityId
    ? workflow.value.schemas?.find((entity) => entity.id === matchingFieldInEntity?.relatedEntityId)
    : true

  return !!commandFieldRelatedEntity && !!entityFieldRelatedEntity
}

const getEntityFieldRelatedEntity = (field) => {
  const matchingFieldInEntity = getMatchingFieldInEntity(currentSystemEntitiesData?.rootEntity, field.name)
  if (!matchingFieldInEntity?.relatedEntityId) {
    return null
  }
  return workflow.value.schemas?.find((entity) => entity.id === matchingFieldInEntity.relatedEntityId)
}

const fixEntityReferenceMismatch = async (field, fieldIndex) => {
  const matchingFieldInEntity = getMatchingFieldInEntity(currentSystemEntitiesData?.rootEntity, field.name)
  if (!matchingFieldInEntity) {
    return
  }

  const card = system.actions[event.index]
  const svgCard = svgGraph.value[event.id]?.cards?.find((c) => c.id === card.id)
  if (!svgCard?.schemaId) {
    return
  }

  const schemas = safeStructuredClone(workflow.value.schemas || [])
  const schemaIndex = schemas.findIndex((s) => s.id === svgCard.schemaId)
  if (schemaIndex === -1) {
    return
  }

  if (!schemas[schemaIndex].fields || fieldIndex < 0 || fieldIndex >= schemas[schemaIndex].fields.length) {
    return
  }

  schemas[schemaIndex].fields[fieldIndex].relatedEntityId = matchingFieldInEntity.relatedEntityId || null

  await workflowApi.updateSchemas({
    schemas,
    workflow: workflow.value
  })
}

watch(() => system.events, () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      initSortable()
    })
  })
})

onMounted(() => {
  initSortable()
})

onBeforeUnmount(() => {
  destroySortable()
})
</script>

<style scoped>
.textEditor.ql-editor.ql-container {
  min-height: 31px;
}

.card-menu .v-list-item:hover{
  background-color: var(--light-2) !important;
}

.v-menu > .v-overlay__content > .v-list {
  padding: 4px 0 !important;
}

.required-field {
  &::after {
    content: ' *';
  }

  &:focus::after {
    visibility: hidden;
  }
}

.sortable-chosen {
  background: var(--light-3);
}

.sortable-ghost {
  background: var(--light-3);
}

.sortable-ghost > div {
  opacity: 0.01;
}

.sortableRow:hover > div > .btn-add-sub-field {
  opacity: 1;
  height: 16px;

  .v-btn {
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }
  }
}

.btn-add-sub-field {
  opacity: 0;
  height: 0;
  transition: all 0.2s;
}
</style>
